<template>
  <div
    class="px-4 py-5 rounded-xl border"
    :class="[
      bankIsExpired(
        props.bank?.created_date,
        props.bank?.financial_institution_name,
        props.bank?.plaid_account_id
      )
        ? '!border-outline-error'
        : '',
      props.transparentBackground
        ? 'bg-transparent border-transparent'
        : 'bg-bg-canvas border-bg-canvas',
    ]"
  >
    <AsteriskNotice
      v-if="
        isExpirationWithinNext3Months(
          props.bank?.created_date,
          props.bank?.financial_institution_name,
          props.bank?.plaid_account_id
        )
      "
      variant="error"
      size="medium"
      class="mb-4"
      :text="
        'Bank account set to expire in ' +
        getMonthsUntilExpiration(props.bank?.created_date) +
        ': Reconnect bank account'
      "
    />
    <AsteriskNotice
      v-else-if="
        bankIsExpired(
          props.bank?.created_date,
          props.bank?.financial_institution_name,
          props.bank?.plaid_account_id
        )
      "
      variant="error"
      size="medium"
      class="mb-4"
      :text="'Expired connection: Reconnect bank account'"
    />
    <div class="items-center flex justify-between">
      <div class="flex items-center">
        <div>
          <div v-if="props.bank.logo">
            <img
              class="h-12 w-12"
              :src="'data:image/png;base64, ' + props.bank.logo + ''"
              alt="Bank Logo"
            />
          </div>
          <div v-else-if="props.bank.type == 'Check'">
            <img :src="checkImg" class="w-12 h-12" alt="Check" />
          </div>
          <div v-else-if="props.bank.type == 'Wire'">
            <img :src="wireImg" class="w-12 h-12" alt="Wire" />
          </div>
          <div v-else-if="props.bank.type == 'Card'">
            <img :src="cardImg" class="w-12 h-12" alt="Card" />
          </div>
          <div v-else-if="props.bank.type == 'Existing Investment'">
            <Icon name="DollarSign" class="w-12 h-12 text-outline-darker" />
          </div>

          <div v-else-if="props.bank.type">
            <Icon name="HandCoins" class="w-12 h-12 text-outline-darker" />
          </div>
          <div v-else>
            <img
              v-if="props.bank.logo"
              class="h-16 w-16"
              :src="'data:image/png;base64, ' + props.bank.logo + ''"
              alt="Red dot"
            />
            <Icon v-else name="Landmark" class="w-12 h-12 text-outline-darker" />
          </div>
        </div>
        <div class="ml-5">
          <div v-if="props.bank.financial_institution_name">
            <div class="text-sm font-inter text-strong">
              {{ props.bank?.financial_institution_name }} - {{ props.bank?.account_type }} account
            </div>
            <div clas="text-base font-inter text-strongest">
              Account ending in *****{{ getLastFour(props.bank?.account_number) }}
            </div>
          </div>
          <div v-else-if="props.bank.type">
            <div class="text-lg font-inter text-strongest">
              {{ props.bank?.type }}
            </div>
          </div>
          <div v-else>
            <div class="text-lg font-inter text-strongest">Unknown Account Type</div>
          </div>
        </div>
      </div>
      <div class="flex flex-row gap-4">
        <Button
          variant="paper-primary"
          v-if="
            isExpirationWithinNext3Months(
              props.bank?.created_date,
              props.bank?.financial_institution_name,
              props.bank?.plaid_account_id
            ) ||
            bankIsExpired(
              props.bank?.created_date,
              props.bank?.financial_institution_name,
              props.bank?.plaid_account_id
            )
          "
          @click="plaidLink.open()"
        >
          <template #label>Reconnect</template>
        </Button>
        <Button variant="paper-primary" v-if="props.changeBankButton" @click="plaidLink.open()">
          <template #label>Change</template>
        </Button>
        <Button variant="paper-primary" v-if="props.customCta" @click="$emit('cta-click')">
          <template #label>{{ props.customCtaLabel || "Change" }}</template>
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Button, Icon, AsteriskNotice } from "@/components";
import {
  getLastFour,
  bankIsExpired,
  isExpirationWithinNext3Months,
  getMonthsUntilExpiration,
} from "@/utilities";
import { usePlaidLink } from "@/composables/usePlaidLink";
import wireImg from "@/assets/images/wire.png";
import checkImg from "@/assets/images/check.png";
import cardImg from "@/assets/images/card.png";
import { onMounted } from "vue";

onMounted(() => {
  plaidLink.initialize();
});

const plaidLink = usePlaidLink();

const props = defineProps({
  bank: {
    type: Object,
    required: true,
  },
  transparentBackground: {
    type: Boolean,
    default: false,
  },
  changeBankButton: {
    type: Boolean,
    default: false,
  },
  customCta: {
    type: Boolean,
    default: false,
  },
  customCtaLabel: {
    type: String,
    default: "",
  },
});
</script>
